import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { type RunContextItemOf } from '../../../store/customWorkflow/customWorkflows'
import { useGetExternalUserByIdQuery } from '../../../store/externalUser/externalUser'
import { useGetCrossClientQueryExecutionQuery } from '../../../store/crossClient/crossClient'
import { useGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import { useLazyGetAiAgentQuery } from '../../../store/ai/aiAgentModel'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { meetingPrepPath, pastMeetingPath } from '../../../views/Scheduling/shared/bookingNavigation'
import { useGetRoomSessionQuery } from '../../../store/media/media'
import { useGetDocumentByIdQuery } from '../../../store/ai/documents'
import { isLiveSession } from '../../../common/session.util'
import { contextKindLabel, insightsScopeLabel, type LabelableContextItem } from './contextLabels'
import { parseMetadata } from '../../../common/util'

export const ContextLabel = ({ item }: { item: LabelableContextItem; accountId: string }): React.ReactElement => (
  <span className='paragraph-small zocks-text-gray-600'>{contextKindLabel(item)}</span>
)

export const ContactLink = ({ item, accountId }: { item: RunContextItemOf<'contact'>; accountId: string }): React.ReactElement => {
  const { data } = useGetExternalUserByIdQuery({ accountId, userId: item.contactId })
  const name = getUserDisplayName(data?.data)
  if (!name) return <ContextLabel item={item} accountId={accountId} />
  return (
    <Link className='zocks-text-primary' target='_blank' rel='noreferrer' to={`/library/manage-contacts/contacts/${item.contactId}`}>
      {name}
    </Link>
  )
}

const CcqClientLink = ({ clientId, accountId }: { clientId: string; accountId: string }): React.ReactElement => {
  const { data } = useGetExternalUserByIdQuery({ accountId, userId: clientId }, { skip: !clientId })
  return (
    <Link className='zocks-text-primary' target='_blank' rel='noreferrer' to={`/library/manage-contacts/contacts/${clientId}`}>
      {getUserDisplayName(data?.data) || clientId}
    </Link>
  )
}

export const CcqExecutionLink = ({ item, accountId }: { item: RunContextItemOf<'ccqExecution'>; accountId: string }): React.ReactElement => {
  const { data } = useGetCrossClientQueryExecutionQuery({ accountId, executionId: item.executionId })
  const question = data?.userQuestion
  const clientIds = item.clientIds

  if (!question && clientIds.length === 0) return <ContextLabel item={item} accountId={accountId} />

  return (
    <span className='d-flex flex-column zocks-gap-1'>
      {question ? (
        <Link className='zocks-text-primary' target='_blank' rel='noreferrer' to={`/cross-client/executions/${item.executionId}`}>
          {`Client Query, question: ${question}`}
        </Link>
      ) : (
        <ContextLabel item={item} accountId={accountId} />
      )}
      {clientIds.length > 0 && (
        <span className='d-flex flex-wrap zocks-gap-1'>
          {clientIds.map((clientId, index) => (
            <React.Fragment key={clientId}>
              {index > 0 && <span>{', '}</span>}
              <CcqClientLink clientId={clientId} accountId={accountId} />
            </React.Fragment>
          ))}
        </span>
      )}
    </span>
  )
}

const DocumentNameLink = ({ documentId, accountId, fallback }: { documentId: string; accountId: string; fallback: string }): React.ReactElement => {
  const { data } = useGetDocumentByIdQuery({ accountId, documentId })
  if (!data) return <span className='paragraph-small zocks-text-gray-600'>{fallback}</span>
  return (
    <Link className='zocks-text-primary' target='_blank' rel='noreferrer' to={`/documents/${documentId}`}>
      {data.name || fallback}
    </Link>
  )
}

export const DocumentLink = ({ item, accountId }: { item: RunContextItemOf<'document'>; accountId: string }): React.ReactElement => (
  <DocumentNameLink documentId={item.documentId} accountId={accountId} fallback={contextKindLabel(item)} />
)

type AgentTypeStrategy = (props: { item: RunContextItemOf<'aiAgentResult'>; accountId: string }) => React.ReactElement

const createMeetingLinkStrategy = (pathFn: (roomId: string, sessionId: string) => string): AgentTypeStrategy => {
  const MeetingLinkStrategy: AgentTypeStrategy = ({ item, accountId }) => {
    const { data: result } = useGetAiAgentResultQuery(
      { accountId, type: item.agentType ?? '', resultKey: item.aiAgentResultKey ?? '' },
      { skip: !item.agentType || !item.aiAgentResultKey },
    )
    const [triggerGetAiAgent, { data: agent }] = useLazyGetAiAgentQuery()
    const originalAiAgentId = result?.originalAiAgentId

    useEffect(() => {
      if (originalAiAgentId) {
        void triggerGetAiAgent({ accountId, aiAgentId: originalAiAgentId })
      }
    }, [accountId, originalAiAgentId, triggerGetAiAgent])

    const context = agent?.context
    const roomId = context?.roomId ?? context?.room_id
    const sessionId = context?.sessionId ?? context?.session_id

    if (!roomId || !sessionId) return <ContextLabel item={item} accountId={accountId} />
    return (
      <Link className='zocks-text-primary' target='_blank' rel='noreferrer' to={pathFn(roomId, sessionId)}>
        {contextKindLabel(item)}
      </Link>
    )
  }
  return MeetingLinkStrategy
}

const MeetingSummaryStrategy = createMeetingLinkStrategy(pastMeetingPath)
const MeetingPreparationStrategy = createMeetingLinkStrategy(meetingPrepPath)

const DocumentSummaryStrategy: AgentTypeStrategy = ({ item, accountId }) => {
  if (!item.aiAgentResultKey) return <ContextLabel item={item} accountId={accountId} />
  return <DocumentNameLink documentId={item.aiAgentResultKey} accountId={accountId} fallback={contextKindLabel(item)} />
}

const AGENT_TYPE_STRATEGIES: Record<string, AgentTypeStrategy> = {
  meetingSummary: MeetingSummaryStrategy,
  meetingPreparation: MeetingPreparationStrategy,
  documentSummary: DocumentSummaryStrategy,
}

export const AiAgentResultLink = ({ item, accountId }: { item: RunContextItemOf<'aiAgentResult'>; accountId: string }): React.ReactElement => {
  const Strategy = item.agentType && Object.hasOwn(AGENT_TYPE_STRATEGIES, item.agentType) ? AGENT_TYPE_STRATEGIES[item.agentType] : undefined
  if (!Strategy) return <ContextLabel item={item} accountId={accountId} />
  return <Strategy item={item} accountId={accountId} />
}

export const MeetingLink = ({ item, accountId }: { item: RunContextItemOf<'meeting'>; accountId: string }): React.ReactElement => {
  const { roomId, sessionId } = item
  const { data, isFetching } = useGetRoomSessionQuery({ accountId, roomId, sessionId })

  if (isFetching || !data?.data) return <ContextLabel item={item} accountId={accountId} />

  const href = isLiveSession(data.data) ? meetingPrepPath(roomId, sessionId) : pastMeetingPath(roomId, sessionId)
  const meetingTitle = parseMetadata(data.data.metadata)?.external?.title
  return (
    <Link className='zocks-text-primary' target='_blank' rel='noreferrer' to={href}>
      {meetingTitle || contextKindLabel(item)}
    </Link>
  )
}

export const InsightsScope = ({ item }: { item: RunContextItemOf<'insights'>; accountId: string }): React.ReactElement => (
  <span className='paragraph-small zocks-text-gray-600'>{insightsScopeLabel(item)}</span>
)
